import { useEffect, useState } from "react";
import {
  type ClienteNexo,
  type EventoJusticiaEntidad,
  type ExtraccionEntidad,
  type FichaEntidad,
  type TimelineJusticiaEntidad,
  justiciaEntidad,
  vizEntidad,
  vizEntidadExtraccion,
  vizEntidadRedRegistro,
  vizEntidadGrafoSiger,
  vizEntidadPorQue,
  type PorQueOfac as DatosPorQue,
  type RedRegistro as DatosRedRegistro,
  type GrafoSiger as DatosGrafoSiger,
} from "../api.ts";
import { C, autoridadEs, etiquetaListaSat, fecha, schemaEs, st } from "../theme.ts";
import { Cargando, ChipFlag, ChipPrograma, ChipSchema, ErrorBox, celdaTd, celdaTh } from "../ui/componentes.tsx";
import { rutaAHash } from "../rutas.ts";
import { DistintivoRegistro, RedRegistro, SegunOfacSeccion } from "../ui/RedRegistro.tsx";
import { GrafoSiger } from "../ui/GrafoSiger.tsx";
import { PorQueOfac, tienePorQue } from "../ui/PorQueOfac.tsx";
import { FichaHallazgos } from "../ficha/Ficha.tsx";

const NOMBRE_TIPO_ES: Record<string, string> = {
  primary: "principal",
  aka: "alias (a.k.a.)",
  fka: "antes conocido como",
  nka: "ahora conocido como",
};

const ID_TIPO_ES: Record<string, string> = {
  ofac_uid: "UID OFAC",
  rfc: "RFC",
  curp: "CURP",
  pasaporte: "Pasaporte",
  folio_mercantil: "Folio en el Registro Público de Comercio",
  company_number: "Nº de registro",
  lei: "LEI",
  otro: "Otro",
};

const FUENTE_ES: Record<string, string> = {
  us_ofac_sdn: "Lista de OFAC",
  sat_69b: "Lista del SAT de empresas que emiten facturas falsas",
  sat_69: "SAT Art. 69",
  us_fincen: "Oficina del Tesoro contra el lavado",
  us_doj_news: "Department of Justice",
  us_fbi_wanted: "FBI Wanted",
  us_federal_register: "Federal Register",
  us_treasury_ofac: "Departamento del Tesoro (comunicados)",
};

const POSTURA_JUSTICIA_ES: Record<string, string> = {
  government_allegation: "Alegación de autoridad",
  defendant_admission: "Admisión de la parte",
  jury_verdict: "Veredicto de jurado",
  court_finding: "Determinación judicial",
  court_order: "Orden judicial",
  unknown: "Postura por determinar",
};

/**
 * Ficha — el dossier de una entidad: quién es (nombres, identificadores),
 * por qué está aquí (sanciones vigentes e históricas, procedencia) y con
 * quién se conecta (aristas en ambas direcciones, entrada a la Red).
 */
export function Ficha({ cliente, id, actor }: { cliente: ClienteNexo; id: string; actor?: string | undefined }) {
  const [ficha, setFicha] = useState<FichaEntidad | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [justicia, setJusticia] = useState<TimelineJusticiaEntidad | null | undefined>(undefined);
  const [errorJusticia, setErrorJusticia] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setFicha(undefined);
    vizEntidad(cliente, id)
      .then((f) => {
        if (vivo) setFicha(f);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente, id]);

  useEffect(() => {
    let vivo = true;
    setJusticia(undefined);
    setErrorJusticia(null);
    justiciaEntidad(cliente, id)
      .then((timeline) => {
        if (vivo) setJusticia(timeline);
      })
      .catch((e: unknown) => {
        if (vivo) setErrorJusticia(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente, id]);

  const [extraccion, setExtraccion] = useState<ExtraccionEntidad | null>(null);
  useEffect(() => {
    let vivo = true;
    setExtraccion(null);
    // Sección opcional: si la migración 0097 no está desplegada o la entidad
    // no tiene menciones documentales, simplemente no se muestra.
    vizEntidadExtraccion(cliente, id)
      .then((x) => {
        // Lo del SIGER (extractor anterior) ya está en la estructura SIGER o en el árbol anterior: aquí solo otras fuentes.
        const noSiger = (d: string) => !d.startsWith("mx_siger:");
        const y = { menciones: x.menciones.filter((m) => noSiger(m.documento_id)), afirmaciones: x.afirmaciones.filter((a) => noSiger(a.documento_id)) };
        if (vivo && (y.menciones.length > 0 || y.afirmaciones.length > 0)) setExtraccion(y);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [cliente, id]);

  const [redRegistro, setRedRegistro] = useState<DatosRedRegistro | null>(null);
  const [errorRegistro, setErrorRegistro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    setRedRegistro(null);
    setErrorRegistro(null);
    // Sección opcional: sólo aparece si la entidad tiene identidad en el registro de comercio (corrida vigente).
    // Si la lectura falla, se dice: no mostrar nada parecería «sin registro».
    vizEntidadRedRegistro(cliente, id)
      .then((x) => {
        if (vivo && (x.resumen || (x.segunOfac?.total ?? 0) > 0)) setRedRegistro(x);
      })
      .catch((e: unknown) => {
        if (vivo) setErrorRegistro(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente, id]);

  const [porQue, setPorQue] = useState<DatosPorQue | null>(null);
  useEffect(() => {
    let vivo = true;
    setPorQue(null);
    // Sección opcional: si la lectura falla o no hay nada que decir, no se muestra (la tabla de sanciones sigue abajo).
    vizEntidadPorQue(cliente, id)
      .then((x) => {
        if (vivo && tienePorQue(x)) setPorQue(x);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [cliente, id]);

  // Etapa 4: mientras la ficha nueva carga o si se pintó, lo viejo no da su propia respuesta (distintivo y «Qué hacer» del registro);
  // si falla, se ve como antes.
  const [fichaNueva, setFichaNueva] = useState<"cargando" | "lista" | "error">("cargando");
  const sinRespuestaVieja = fichaNueva !== "error";
  const [grafoSiger, setGrafoSiger] = useState<DatosGrafoSiger | null>(null);
  useEffect(() => {
    let vivo = true;
    setGrafoSiger(null);
    // Sección opcional: solo si la entidad tiene vínculos en la corrida vigente del grafo SIGER (Claude).
    vizEntidadGrafoSiger(cliente, id)
      .then((x) => {
        if (vivo && x && (x.vinculos.length > 0 || x.anclas.length > 0)) setGrafoSiger(x);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [cliente, id]);
  // El dibujo trae como máximo las ligas más fuertes; `totales_tipo` cuenta todas las de la corrida (los mismos conteos que la
  // lista): si faltan, se dice. Etapa 4: sin el respaldo de las letras A–D.
  const totalLigasGrafo = (grafoSiger?.totales_tipo ?? []).reduce((a, t) => a + t.ligas, 0);

  if (error) return <ErrorBox mensaje={error} />;
  if (ficha === undefined) return <Cargando texto="Cargando la ficha…" />;
  if (ficha === null) return <ErrorBox mensaje="No existe una entidad con ese identificador." />;

  const e = ficha.entidad;
  // Dos ejes (§7.7): sanciones = SOLO eje red (la compat 0075 mete los flags
  // como pseudo-sanciones es_flag=true — aquí se separan de vuelta).
  const sancionesReales = ficha.sanciones.filter((s) => !s.es_flag);
  const sancionesVigentes = sancionesReales.filter((s) => s.vigente);
  const flagsVigentes = ficha.flags.filter((f) => f.vigente);
  const alias = ficha.nombres.filter((n) => n.tipo !== "primary");
  const props = e.props;
  const remarks = typeof props["remarks"] === "string" ? props["remarks"] : null;
  const title = typeof props["title"] === "string" ? props["title"] : null;

  return (
    <div>
      <a
        href={rutaAHash({ vista: "explorador" })}
        style={{ fontSize: 13, color: C.tiel, textDecoration: "none", fontWeight: 600 }}
      >
        ← Explorador
      </a>

      <div style={{ ...st.tarjeta, marginTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ ...st.display, fontSize: 26, margin: "0 0 6px" }}>{e.caption ?? "(sin nombre)"}</h1>
            <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <ChipSchema schema={e.schema} />
              {e.pais ? <span style={{ fontSize: 12.5, color: C.muted }}>País: {e.pais}</span> : null}
              {title ? <span style={{ fontSize: 12.5, color: C.muted }}>{title}</span> : null}
              {!e.vigente ? (
                <span style={{ fontSize: 12, fontWeight: 700, color: C.amber }}>versión no vigente</span>
              ) : null}
              {redRegistro?.resumen && !sinRespuestaVieja ? (
                <DistintivoRegistro resumen={redRegistro.resumen} onClick={() => { document.getElementById("registro-comercio")?.scrollIntoView({ behavior: "smooth" }); }} />
              ) : null}
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
              {sancionesVigentes.map((s, i) => <ChipPrograma key={i} programa={s.programa} />)}
              {sancionesVigentes.length === 0 ? (
                <span style={{ fontSize: 12.5, color: C.green, fontWeight: 700 }}>
                  {sancionesReales.length > 0 ? "Sin sanción vigente: la sacaron de la lista" : "Sin sanción (eje red)"}
                </span>
              ) : null}
              {flagsVigentes.map((f, i) => <ChipFlag key={`f${String(i)}`} flag={f} />)}
            </div>
          </div>
          {ficha.aristas.length > 0 ? (
            <a
              href={rutaAHash({ vista: "red", id: e.id, saltos: 2 })}
              style={{ ...st.btnPrim, textDecoration: "none", alignSelf: "flex-start", whiteSpace: "nowrap" }}
            >
              Ver red ({String(ficha.aristas.length)})
            </a>
          ) : null}
        </div>
        {remarks ? (
          <p style={{ fontSize: 13, color: C.muted, margin: "12px 0 0", lineHeight: 1.5 }}>{remarks}</p>
        ) : null}
      </div>

      {/* Etapa 4: primero lo que Argus afirma de esta persona o empresa (nexo.hallazgos), con las piezas y el orden de la ficha de
          Argus Team. Debajo sigue lo que la ficha nueva todavía no reemplaza: por qué la designó OFAC, sanciones e identificadores,
          la red en el registro de comercio alrededor de ella (dibujo y frases), lo que declara OFAC y los documentos. */}
      <div style={{ ...st.tarjeta, marginTop: 12 }}>
        <FichaHallazgos
          key={id}
          cliente={cliente}
          sujeto={{ kind: "entity", id }}
          mostrarNombre={false}
          onEstado={setFichaNueva}
          onBuscarDeNuevo={() => { window.location.hash = rutaAHash({ vista: "explorador" }); }}
        />
      </div>

      {/* Bernardo, 2026-10-01: arriba, por qué la designó OFAC; luego sanciones e identificadores, el registro de comercio
          (dibujo y frases), lo que declara OFAC y los documentos. */}
      {porQue ? <PorQueOfac cliente={cliente} datos={porQue} /> : null}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12 }}>
        <div style={{ ...st.tarjeta, marginBottom: 0 }}>
          <p style={st.eyebrow}>Sanciones — eje red ({String(sancionesReales.length)})</p>
          {sancionesReales.length === 0 ? (
            <p style={{ fontSize: 13, color: C.muted }}>
              Sin sanciones de OFAC ni de la Oficina del Tesoro contra el lavado. La situación fiscal
              ante el SAT va aparte: es otro eje de riesgo.
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={celdaTh}>Programa</th>
                  <th style={celdaTh}>Autoridad</th>
                  <th style={celdaTh}>Fecha en la lista</th>
                  <th style={celdaTh}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {sancionesReales.map((s, i) => (
                  <tr key={i}>
                    <td style={celdaTd}><ChipPrograma programa={s.programa} /></td>
                    <td style={celdaTd}>{autoridadEs(s.autoridad)}</td>
                    <td style={{ ...celdaTd, whiteSpace: "nowrap" }}>
                      {s.listing_date ? fecha(s.listing_date) : (
                        <span title={`Nexo la observó desde ${fecha(s.valid_from)}; OFAC no entregó fecha original en esta carga.`}>
                          Sin fecha oficial
                        </span>
                      )}
                    </td>
                    <td style={celdaTd}>
                      {s.vigente
                        ? <span style={{ color: C.red, fontWeight: 700, fontSize: 12.5 }}>Vigente</span>
                        : <span style={{ color: C.muted, fontSize: 12.5 }}>Cerrada {fecha(s.valid_to)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {ficha.flags.length > 0 ? (
            <>
              <p style={{ ...st.eyebrow, marginTop: 14 }}>Situación fiscal SAT — eje propio ({String(ficha.flags.length)})</p>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={celdaTh}>Lista</th>
                    <th style={celdaTh}>Detalle</th>
                    <th style={celdaTh}>Publicación</th>
                    <th style={celdaTh}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {ficha.flags.map((f, i) => (
                    <tr key={i}>
                      <td style={celdaTd}><ChipFlag flag={f} /></td>
                      <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>
                        {f.supuesto ?? ""}
                        {f.entidad_federativa ? (f.supuesto ? " · " : "") + f.entidad_federativa : ""}
                      </td>
                      <td style={{ ...celdaTd, whiteSpace: "nowrap" }}>{fecha(f.fecha_publicacion ?? f.valid_from)}</td>
                      <td style={celdaTd}>
                        {f.vigente
                          ? <span style={{ color: C.amber, fontWeight: 700, fontSize: 12.5 }}>Vigente</span>
                          : <span style={{ color: C.muted, fontSize: 12.5 }}>Cerrado {fecha(f.valid_to)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 0 0" }}>
                Aparece en la lista referida del SAT con la fecha indicada — informa diligencia, no
                constituye acusación. Los estados cerrados/exculpados se preservan como historia.
              </p>
            </>
          ) : null}
        </div>

        <div style={{ ...st.tarjeta, marginBottom: 0 }}>
          <p style={st.eyebrow}>Identificadores ({String(ficha.identificadores.length)})</p>
          {ficha.identificadores.length === 0 ? (
            <p style={{ fontSize: 13, color: C.muted }}>Sin identificadores estructurados.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {ficha.identificadores.map((idf, i) => (
                  <tr key={i}>
                    <td style={{ ...celdaTd, color: C.muted, whiteSpace: "nowrap", fontSize: 12.5 }}>
                      {ID_TIPO_ES[idf.tipo] ?? idf.tipo}
                    </td>
                    <td style={{ ...celdaTd, fontFamily: "ui-monospace, Consolas, monospace", fontSize: 13 }}>
                      {idf.valor}
                      {idf.fraudulento ? (
                        <span style={{ color: C.red, fontWeight: 700, fontSize: 11, marginLeft: 8 }}>FRAUDULENTO</span>
                      ) : null}
                    </td>
                    <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>{idf.pais ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {alias.length > 0 ? (
            <>
              <p style={{ ...st.eyebrow, marginTop: 14 }}>Alias ({String(alias.length)})</p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: C.ink }}>
                {alias.map((n, i) => (
                  <li key={i}>
                    {n.nombre}
                    <span style={{ color: C.muted, fontSize: 11.5 }}> · {NOMBRE_TIPO_ES[n.tipo] ?? n.tipo}</span>
                    {n.low_quality ? (
                      <span style={{ color: C.amber, fontSize: 11.5, fontWeight: 700 }}> · alias débil</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>

      {ficha.aristas.length > 0 ? (
      <div style={{ ...st.tarjeta, marginTop: 12 }}>
        <p style={st.eyebrow}>Relaciones ({String(ficha.aristas.length)})</p>
        {(
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={celdaTh}>Dirección</th>
                <th style={celdaTh}>Con quién</th>
                <th style={celdaTh}>Tipo</th>
                <th style={celdaTh}>Relación</th>
              </tr>
            </thead>
            <tbody>
              {ficha.aristas.map((a) => (
                <tr key={a.id}>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap", color: C.muted, fontSize: 12.5 }}>
                    {a.dir === "sale" ? "relación hacia →" : "← relación desde"}
                  </td>
                  <td style={{ ...celdaTd, fontWeight: 600 }}>
                    <a
                      href={rutaAHash({ vista: "entidad", id: a.contraparte_id })}
                      style={{ color: C.tiel, textDecoration: "none" }}
                    >
                      {a.contraparte_caption ?? a.contraparte_id}
                    </a>
                    {a.contraparte_sancionada ? (
                      <span title="Con sanción vigente" style={{ color: C.red, marginLeft: 6 }}>●</span>
                    ) : null}
                  </td>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap" }}><ChipSchema schema={a.contraparte_schema} /></td>
                  <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>
                    {a.rol ?? (a.schema === "LinkedTo" ? "Linked To (OFAC, sin tipo)" : a.schema)}
                    {a.porcentaje !== null ? ` · ${String(a.porcentaje)}%` : ""}
                    {a.calidad === "High" ? (
                      <span style={{ color: C.green, fontWeight: 700, fontSize: 11 }}> · calidad alta</span>
                    ) : null}
                    {a.calidad === "Low" ? (
                      <span style={{ color: C.amber, fontWeight: 700, fontSize: 11 }}> · calidad baja</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      ) : null}

      {ficha.cruce.length > 0 ? (
        <div style={{ ...st.tarjeta, marginTop: 12 }}>
          <p style={st.eyebrow}>Cruce de listas: SAT ↔ OFAC y Oficina del Tesoro contra el lavado ({String(ficha.cruce.length)})</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={celdaTh}>Certeza</th>
                <th style={celdaTh}>Lado SAT</th>
                <th style={celdaTh}>Lado sancionado</th>
                <th style={celdaTh}>Cómo matcheó</th>
              </tr>
            </thead>
            <tbody>
              {ficha.cruce.map((c, i) => (
                <tr key={i}>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap" }}>
                    {c.confirmado ? (
                      <span style={{ color: C.red, fontWeight: 700, fontSize: 12.5 }}>CONFIRMADO</span>
                    ) : (
                      <span style={{ color: C.amber, fontWeight: 700, fontSize: 12.5 }}>Posible coincidencia</span>
                    )}
                  </td>
                  <td style={celdaTd}>
                    <span style={{ fontFamily: "ui-monospace, Consolas, monospace", fontSize: 12.5 }}>{c.rfc}</span>
                    {c.nombre_sat ? <span style={{ color: C.muted, fontSize: 12.5 }}> · {c.nombre_sat}</span> : null}
                    <span style={{ color: C.muted, fontSize: 11.5 }}>
                      {" "}({c.listas.map((l) => etiquetaListaSat(l.lista, l.categoria)).join(", ")})
                    </span>
                  </td>
                  <td style={{ ...celdaTd, fontWeight: 600 }}>
                    <a href={rutaAHash({ vista: "entidad", id: c.entidad_id })} style={{ color: C.tiel, textDecoration: "none" }}>
                      {c.entidad_nombre ?? c.entidad_id}
                    </a>
                    <span style={{ color: C.muted, fontSize: 11.5 }}> · {c.autoridades.map(autoridadEs).join(" + ")}</span>
                  </td>
                  <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>
                    {c.metodo === "rfc" ? "Mismo RFC (determinístico)" : null}
                    {c.metodo === "nombre_exacto" ? "Nombre idéntico — sin RFC que lo confirme" : null}
                    {c.metodo === "nombre_similar"
                      ? `Nombre similar (score ${String(c.score)}) — sin RFC que lo confirme`
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 0 0" }}>
            Una coincidencia por nombre puede ser otra persona con el mismo nombre: verifica con RFC o folio
            en el Registro Público de Comercio antes de decidir. Sólo el cruce por RFC identifica al mismo
            contribuyente.
          </p>
        </div>
      ) : null}

      {/* Bernardo, 2026-10-01: el dibujo y la lista son una sola sección, «Registro de comercio», con los mismos grupos. */}
      {(() => {
        const dibujo = grafoSiger ? (
          <div id="grafo-siger">
            {totalLigasGrafo > grafoSiger.vinculos.length ? (
              <p style={{ fontSize: 12, color: C.muted, margin: "12px 0 0" }}>
                El dibujo muestra {String(grafoSiger.vinculos.length)} de {String(totalLigasGrafo)} relaciones, las de más peso. Todas están en la lista de abajo.
              </p>
            ) : null}
            <GrafoSiger key={actor ?? ""} datos={grafoSiger} entidadId={id} actorInicial={actor} raizNombre={e.caption ?? "(sin nombre)"} incrustado={!!redRegistro?.resumen} />
          </div>
        ) : null;
        if (redRegistro?.resumen) return <div id="registro-comercio"><RedRegistro datos={redRegistro} dibujo={dibujo} sinRespuesta={sinRespuestaVieja} /></div>;
        return (
          <>
            {dibujo}
            {errorRegistro ? (
              <p style={{ ...st.tarjeta, marginTop: 12, fontSize: 13, color: C.amber }}>
                No se pudo leer el Registro Público de Comercio de esta entidad ({errorRegistro}). Esto no significa que no aparezca en él.
              </p>
            ) : null}
          </>
        );
      })()}

      {/* Bernardo, 2026-10-01: primero el dibujo de la red; la lista de frases va debajo; después lo que declara OFAC. */}
      {redRegistro?.segunOfac ? <SegunOfacSeccion datos={redRegistro.segunOfac} /> : null}

      {errorJusticia || (justicia && (justicia.approved.length > 0 || justicia.pending.length > 0)) ? (
        <SeccionJusticia timeline={justicia} error={errorJusticia} />
      ) : null}

      {extraccion ? <SeccionExtraccion extraccion={extraccion} /> : null}

      {ficha.documentos.length > 0 ? (
        <div style={{ ...st.tarjeta, marginTop: 12 }}>
          <p style={st.eyebrow}>Documentos que la mencionan ({String(ficha.documentos.length)})</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {ficha.documentos.map((d) => (
                <tr key={d.id}>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap", color: C.muted, fontSize: 12.5 }}>{fecha(d.fecha)}</td>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap", fontSize: 12 }}>
                    <span style={{ fontWeight: 700 }}>{autoridadEs(d.autoridad)}</span>
                    <span style={{ color: C.muted }}> · {d.tipo}</span>
                  </td>
                  <td style={celdaTd}>
                    <a
                      href={rutaAHash({ vista: "documento", id: d.id })}
                      style={{ color: C.tiel, textDecoration: "none", fontWeight: 600, fontSize: 13 }}
                    >
                      {d.titulo}
                    </a>
                    {d.sar_key_term ? (
                      <span style={{ color: C.muted, fontSize: 11.5, fontFamily: "ui-monospace, Consolas, monospace" }}>
                        {" "}· {d.sar_key_term}
                      </span>
                    ) : null}
                  </td>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap", fontSize: 11.5, color: d.rol === "designado" ? C.red : C.muted, fontWeight: d.rol === "designado" ? 700 : 400 }}>
                    {d.rol === "sujeto_medida" ? "objeto de la medida" : d.rol === "designado" ? "sancionado" : d.rol}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <p style={{ fontSize: 11.5, color: C.muted, margin: "10px 2px" }}>
        Procedencia: {ficha.xrefs.map((x) => `${FUENTE_ES[x.fuente_id] ?? x.fuente_id} (${x.native_id})`).join(" · ")}
        {" · "}Tipo FtM: {schemaEs(e.schema)} ({e.schema}) · Visto por primera vez {fecha(e.first_seen)}.
      </p>
    </div>
  );
}

function SeccionJusticia({
  timeline,
  error,
}: {
  timeline: TimelineJusticiaEntidad | null | undefined;
  error: string | null;
}) {
  return (
    <div style={{ ...st.tarjeta, marginTop: 12 }}>
      <p style={st.eyebrow}>Justicia</p>
      <p style={{ fontSize: 11.5, color: C.muted, margin: "0 0 10px" }}>
        Información atribuida a documentos oficiales. Las alegaciones no se presentan como
        condenas y las propuestas pendientes permanecen separadas de los eventos aprobados.
      </p>
      {error ? (
        <p style={{ fontSize: 12.5, color: C.muted, margin: 0 }}>
          Timeline no disponible: {error}. El resto de la ficha permanece operativo.
        </p>
      ) : timeline === undefined ? (
        <Cargando texto="Cargando timeline de Justicia…" />
      ) : timeline === null ? (
        <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Sin timeline para esta entidad.</p>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <GrupoTimeline
            titulo="Eventos revisados y aprobados"
            eventos={timeline.approved}
            pendiente={false}
          />
          <GrupoTimeline
            titulo="Propuestas pendientes de revisión"
            eventos={timeline.pending}
            pendiente
          />
        </div>
      )}
    </div>
  );
}

function GrupoTimeline({
  titulo,
  eventos,
  pendiente,
}: {
  titulo: string;
  eventos: EventoJusticiaEntidad[];
  pendiente: boolean;
}) {
  return (
    <div
      style={{
        border: `1px solid ${pendiente ? C.amber : C.line}`,
        background: pendiente ? C.amberBg : "#fff",
        borderRadius: 10,
        padding: 10,
      }}
    >
      <p style={{ ...st.eyebrow, color: pendiente ? C.amber : C.muted }}>
        {titulo} ({String(eventos.length)})
      </p>
      {eventos.length === 0 ? (
        <p style={{ fontSize: 12.5, color: C.muted, margin: 0 }}>Sin registros.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={celdaTh}>Fecha</th>
              <th style={celdaTh}>Evento atribuido</th>
              <th style={celdaTh}>Postura</th>
              <th style={celdaTh}>Fuente y evidencia</th>
            </tr>
          </thead>
          <tbody>
            {eventos.map((evento) => (
              <tr key={evento.assertion_id}>
                <td style={{ ...celdaTd, whiteSpace: "nowrap" }}>
                  {fecha(evento.event_at ?? evento.reviewed_at ?? evento.retrieved_at)}
                </td>
                <td style={celdaTd}>
                  <div style={{ fontWeight: 600 }}>
                    Según {FUENTE_ES[evento.source_id] ?? evento.source_id}: {(
                      evento.event_type ?? evento.predicate
                    ).replace(/_/g, " ")}
                  </div>
                  <div style={{ color: C.muted, fontSize: 11.5 }}>{evento.document_title}</div>
                  {pendiente ? (
                    <div style={{ color: C.amber, fontSize: 11.5, fontWeight: 700 }}>
                      Pendiente: no es un hecho validado por Argus.
                    </div>
                  ) : null}
                </td>
                <td style={{ ...celdaTd, fontSize: 12.5 }}>
                  {POSTURA_JUSTICIA_ES[evento.posture] ?? evento.posture}
                </td>
                <td style={{ ...celdaTd, fontSize: 12 }}>
                  {evento.source_url ? (
                    <a href={evento.source_url} target="_blank" rel="noreferrer" style={{ color: C.tiel }}>
                      Abrir fuente ↗
                    </a>
                  ) : <span style={{ color: C.muted }}>Sin enlace</span>}
                  {evento.payload_sha256 ? (
                    <div
                      title={evento.payload_sha256}
                      style={{ color: C.muted, fontSize: 10.5, fontFamily: "ui-monospace, Consolas, monospace" }}
                    >
                      SHA-256 {evento.payload_sha256.slice(0, 12)}…
                    </div>
                  ) : null}
                  <div style={{ color: C.ink, fontSize: 11.5, marginTop: 4 }}>
                    “{evento.evidence_excerpt}”
                  </div>
                  <div style={{ color: C.muted, fontSize: 10.5 }}>
                    {locatorJusticia(evento.evidence_locator)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * SeccionExtraccion — capa documental del extractor (0097): qué menciones de
 * documentos del corpus resuelven a esta entidad (candidatos por nombre,
 * nunca vínculos automáticos) y qué afirmaciones extraídas la involucran.
 */
function SeccionExtraccion({ extraccion }: { extraccion: ExtraccionEntidad }) {
  const METODO_ES: Record<string, string> = {
    identifier: "identificador",
    name_exact: "nombre exacto",
    name_similar: "nombre similar",
    manual: "manual",
  };
  return (
    <div style={{ ...st.tarjeta }}>
      <p style={st.eyebrow}>Extracción documental ({String(extraccion.afirmaciones.length)} afirmaciones)</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.5 }}>
        Menciones en documentos del corpus que coinciden con esta entidad por nombre y las
        afirmaciones extraídas con evidencia literal donde participan.
      </p>
      {extraccion.menciones.length > 0 ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {extraccion.menciones.map((m) => (
            <span
              key={m.resolucion_id}
              title={`${m.titulo ?? m.documento_id} · ${METODO_ES[m.metodo] ?? m.metodo}${m.score !== null ? ` · score ${String(m.score)}` : ""}`}
              style={{
                fontSize: 11.5,
                padding: "3px 9px",
                borderRadius: 999,
                border: `1px solid ${m.estado === "approved" ? C.green : C.amber}`,
                color: m.estado === "approved" ? C.green : C.amber,
                fontWeight: 600,
              }}
            >
              “{m.mencion}” · {METODO_ES[m.metodo] ?? m.metodo}
              {m.estado === "approved" ? " ✓" : " (propuesto)"}
            </span>
          ))}
        </div>
      ) : null}
      {extraccion.afirmaciones.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={celdaTh}>Afirmación extraída</th>
              <th style={celdaTh}>Documento</th>
              <th style={celdaTh}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {extraccion.afirmaciones.map((a) => (
              <tr key={a.id}>
                <td style={celdaTd}>
                  <div style={{ fontWeight: 600, fontSize: 12.5 }}>
                    {a.subject_text} · {a.predicate.replace(/_/g, " ")}
                    {a.object_text ? ` · ${a.object_text}` : ""}
                  </div>
                  <div style={{ color: C.muted, fontSize: 11.5, marginTop: 2 }}>“{a.evidence_excerpt}”</div>
                </td>
                <td style={{ ...celdaTd, fontSize: 12 }}>
                  <a
                    href={rutaAHash({ vista: "documento", id: a.documento_id })}
                    style={{ color: C.tiel, textDecoration: "none" }}
                  >
                    {a.titulo ?? a.documento_id}
                  </a>
                </td>
                <td style={{ ...celdaTd, whiteSpace: "nowrap", fontSize: 11.5, color: a.review_status === "approved" ? C.green : C.amber, fontWeight: 700 }}>
                  {a.review_status === "approved" ? "aprobada" : "propuesta"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function locatorJusticia(locator: Record<string, unknown> | null): string {
  if (!locator) return "Sin localizador estructurado";
  const field = typeof locator.field === "string" ? locator.field : null;
  const paragraph = typeof locator.paragraph === "number" ? locator.paragraph + 1 : null;
  const start = typeof locator.start === "number" ? locator.start : null;
  const end = typeof locator.end === "number" ? locator.end : null;
  return [
    field ? `campo ${field}` : null,
    paragraph ? `párrafo ${String(paragraph)}` : null,
    start !== null && end !== null ? `caracteres ${String(start)}–${String(end)}` : null,
  ].filter(Boolean).join(" · ") || JSON.stringify(locator);
}
